import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const BRAND_TINTS = ['#07507A', '#07989A', '#FF8A2B', '#1E9E6A', '#2BB3E6', '#8B5CF6'];

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Deterministic tint so the same name always gets the same color. */
function tintFor(name: string): string {
  let sum = 0;
  for (let i = 0; i < name.length; i += 1) sum += name.charCodeAt(i);
  return BRAND_TINTS[sum % BRAND_TINTS.length];
}

const Box = styled.div<{ $size: number; $bg: string; $shape: 'circle' | 'square'; $clickable: boolean }>`
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: ${(p) => (p.$shape === 'circle' ? '50%' : p.theme.radius)};
  background: ${(p) => p.$bg};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: ${(p) => p.$size * 0.4}px;
  flex-shrink: 0;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
`;

const Img = styled.img<{ $size: number; $shape: 'circle' | 'square'; $clickable: boolean }>`
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: ${(p) => (p.$shape === 'circle' ? '50%' : p.theme.radius)};
  object-fit: cover;
  background: ${(p) => p.theme.surfaceAlt};
  flex-shrink: 0;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
`;

interface AvatarProps {
  name: string;
  src?: string;
  size?: number;
  shape?: 'circle' | 'square';
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * Merchant/user avatar. Shows the image when present; falls back to colored
 * initials when there is no `src` OR the image fails to load.
 */
export const Avatar: React.FC<AvatarProps> = ({ name, src, size = 44, shape = 'square', className, onClick }) => {
  const [failed, setFailed] = useState(false);

  // Reset the error state if the source changes.
  useEffect(() => setFailed(false), [src]);

  if (src && !failed) {
    return (
      <Img
        src={src}
        alt={name}
        $size={size}
        $shape={shape}
        $clickable={!!onClick}
        className={className}
        onClick={onClick}
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <Box
      $size={size}
      $bg={tintFor(name)}
      $shape={shape}
      $clickable={!!onClick}
      className={className}
      onClick={onClick}
    >
      {initials(name)}
    </Box>
  );
};

export default Avatar;
